import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { useLogoUrl } from '../lib/configSistema';
import { ITENS_MENU, itemVisivel } from '../lib/menu';
import AtualizacaoDisponivel from './AtualizacaoDisponivel';
import Breadcrumbs from './Breadcrumbs';
import VoltarAoTopo from './VoltarAoTopo';
import pkg from '../../package.json';
import './Layout.css';

// Logo padrão (mesma do painel antigo) — configurável em Configurações → Aparência (ver `useLogoUrl`,
// 28/09/2026). ATENÇÃO: o wl-node (controllers/agendamento.js) usa esse MESMO caminho fixo, mas
// direto, sem ler essa configuração — trocar o logo aqui não muda o que aparece nas páginas do wl.

// Mesmo ponto de quebra do Layout.css (Bootstrap `md`).
const MEDIA_DESKTOP = '(min-width: 768px)';
const CHAVE_MENU = 'painel:menu-aberto';

function lerPreferenciaMenu() {
  try {
    const salvo = localStorage.getItem(CHAVE_MENU);
    return salvo === null ? true : salvo === 'true';
  } catch {
    return true;
  }
}

function salvarPreferenciaMenu(aberto) {
  try {
    localStorage.setItem(CHAVE_MENU, String(aberto));
  } catch {
    // navegador sem storage (janela anônima etc.): só não lembra a preferência
  }
}

export default function Layout() {
  const { usuario, sair } = useAuth();
  const logoUrl = useLogoUrl();
  const location = useLocation();
  const [ehDesktop, setEhDesktop] = useState(() => window.matchMedia(MEDIA_DESKTOP).matches);
  // Desktop: lembra a última escolha. Celular: sempre começa fechado (é uma gaveta
  // sobre o conteúdo — abrir sozinho cobriria a tela toda).
  const [aberto, setAberto] = useState(() => (window.matchMedia(MEDIA_DESKTOP).matches ? lerPreferenciaMenu() : false));
  // Versão da API só pra conferência visual (rodapé) — se falhar (offline, CORS etc.), some
  // sozinha; nunca trava a tela por causa disso.
  const [versaoApi, setVersaoApi] = useState(null);
  useEffect(() => {
    api.get('/is-online').then((r) => setVersaoApi(r.version)).catch(() => {});
  }, []);

  // Girar o aparelho / redimensionar a janela atravessando o ponto de quebra:
  // recomeça do padrão do novo modo (senão a gaveta do celular ficava "aberta" ao
  // voltar pro desktop, ou o menu recolhido do desktop virava gaveta aberta).
  useEffect(() => {
    const consulta = window.matchMedia(MEDIA_DESKTOP);
    function aoMudar(e) {
      setEhDesktop(e.matches);
      setAberto(e.matches ? lerPreferenciaMenu() : false);
    }
    consulta.addEventListener('change', aoMudar);
    return () => consulta.removeEventListener('change', aoMudar);
  }, []);

  // No celular, navegar pra outra tela fecha a gaveta.
  useEffect(() => {
    if (!window.matchMedia(MEDIA_DESKTOP).matches) setAberto(false);
  }, [location.pathname]);

  // Esc fecha a gaveta (só faz sentido no celular).
  useEffect(() => {
    function aoTeclar(e) {
      if (e.key === 'Escape' && !window.matchMedia(MEDIA_DESKTOP).matches) setAberto(false);
    }
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, []);

  function alternarMenu() {
    setAberto((atual) => {
      const novo = !atual;
      if (ehDesktop) salvarPreferenciaMenu(novo);
      return novo;
    });
  }

  return (
    <>
      <AtualizacaoDisponivel />
      <div className="d-flex" style={{ minHeight: '100vh' }}>
      <div className={`layout-backdrop ${aberto ? 'aberto' : ''}`} onClick={() => setAberto(false)} />

      <aside className={`layout-sidebar d-flex flex-column ${aberto ? 'aberto' : 'recolhido'}`}>
        {/* Fundo branco atrás da logo (achado 2026-09-18: o azul escuro do
            texto da logo ficava ilegível direto sobre o azul escuro da
            sidebar) — mesmo tratamento que o login antigo já dava
            (`.logo{background-color:#fff}` em login.css). Correção
            2026-09-19 (feedback de Jason): no painel Laravel o branco cobre
            a DIV INTEIRA do cabeçalho, borda a borda da sidebar — não só uma
            caixa arredondada justa ao redor da imagem, com a cor escura da
            sidebar sobrando ao redor. Por isso essa div é IRMÃ do `<Nav>`
            (não faz parte do `p-3` dele), com seu próprio padding — só ela
            fica branca, o resto da sidebar continua escuro. */}
        <Navbar.Brand as={Link} to="/" className="d-block text-center m-0" style={{ backgroundColor: '#fff', padding: '16px 8px' }}>
          <img src={logoUrl} alt="EIMOVEL" style={{ maxWidth: 150 }} />
        </Navbar.Brand>
        <Nav className="flex-column p-3" variant="pills">
          {ITENS_MENU.filter((item) => itemVisivel(item, usuario)).map((item) => (
            <Nav.Link
              key={item.rota}
              as={NavLink}
              to={item.rota}
              end={item.rota === '/'}
              className="text-white"
            >
              {item.rotulo}
            </Nav.Link>
          ))}
        </Nav>
      </aside>

      {/* minWidth 0: sem isso, uma tabela larga estica este filho do flex e a página
          inteira ganha rolagem horizontal em vez de só a tabela (`responsive`). */}
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <Navbar bg="white" className="border-bottom px-3">
          <Container fluid className="flex-nowrap">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={alternarMenu}
              aria-label={aberto ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={aberto}
            >
              ☰
            </button>
            <div className="ms-auto d-flex align-items-center">
              <span className="me-3 text-muted d-none d-sm-inline text-truncate" style={{ maxWidth: 200 }}>
                {usuario.nome}
              </span>
              <Link to="/perfil" className="btn btn-sm btn-link me-1">
                Meu perfil
              </Link>
              <Link to="/trocar-senha" className="btn btn-sm btn-link me-1">
                Alterar senha
              </Link>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={sair}>
                Sair
              </button>
            </div>
          </Container>
        </Navbar>
        {/* padding-bottom: espaço no rodapé pra seta de voltar ao topo e o botão de salvar flutuante não taparem o último texto */}
        <Container fluid className="p-3 p-md-4" style={{ paddingBottom: 160 }}>
          <Breadcrumbs />
          <Outlet />
        </Container>
        {/* Versão do sistema no rodapé, como o WordPress mostra — ajuda a confirmar se o deploy pegou (pedido do Jason, 28/09/2026). */}
        <footer className="text-center text-muted small pb-3">
          EIMOVEL — painel v{pkg.version}{versaoApi && ` • API v${versaoApi}`}
        </footer>
        <VoltarAoTopo />
      </div>
      </div>
    </>
  );
}
